/**
 * Flash Sale Stock Validator
 * --------------------------
 * Real-time stock validation layer for the Release theme PDP.
 *
 * Problem solved:
 *   In flash sales, the theme's variant change flow uses Section Rendering API,
 *   which goes through Shopify's edge cache. Stale cache + the theme's reliance on
 *   `selected_or_first_available_variant` causes the wrong variant ID to land in
 *   the form input when a variant sells out mid-session.
 *
 * Strategy:
 *   1. Fetch /products/{handle}.js with cache-bust on init AND every 5s.
 *   2. Disable swatches/buttons (standard picker + Hero Variant Picker) for
 *      option values that resolve to sold-out variants given current selection.
 *   3. Override "Only X left in stock" to show actual current variant's stock.
 *   4. Force-sync the form input[name=id] to the user's *visual* choice after
 *      every theme-driven section swap (post-PUB_SUB_EVENTS.variantChange).
 *   5. Pre-flight check on Add to Cart submit: refresh stock, validate, popup
 *      if race condition hit.
 *   6. PUB_SUB_EVENTS.cartError safety net for 422 from /cart/add.js.
 *
 * Assumptions:
 *   - Products use the standard <product-info data-main-product> custom element.
 *   - inventory_policy = 'deny' (no oversell).
 *   - At most 3 options per product (Shopify's limit).
 *
 * NOT a replacement for the theme's variant flow — runs in parallel and corrects.
 */

(function () {
  'use strict';

  // ---------- CONFIG ----------
  const POLL_INTERVAL_MS = 2000;        // re-check stock every 2s
  const FETCH_TIMEOUT_MS = 4000;        // give up on /products/{handle}.js after 4s
  const POST_RENDER_DELAY_MS = 50;      // wait after theme's viewTransition for DOM to settle

  const SOLD_OUT_CLASS = 'flash-sold-out';
  const POPUP_OPEN_CLASS = 'is-open';

  // DEBUG can be toggled at runtime in DevTools: window.__FSSV_DEBUG = true;
  function log(...args) {
    if (typeof window !== 'undefined' && window.__FSSV_DEBUG === true) {
      console.log('[FSSV]', ...args);
    }
  }

  // ---------- VALIDATOR ----------
  class FlashSaleStockValidator {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.handle = productInfo.dataset.productHandle;
      this.section = productInfo.dataset.section;
      this.url = productInfo.dataset.url;

      this.productData = null;       // cached /products/{handle}.js (real-time)
      this.staticVariants = null;    // cached <script data-all-variants> (Liquid render, fallback)
      this.pollTimer = null;
      this.bypassSubmit = false;     // re-entry guard for form.requestSubmit()
      this.unsubVariant = null;
      this.unsubError = null;
      this.popup = null;

      // pre-bind handlers so we can remove them
      this._onChange = this._onChange.bind(this);
      this._onClickCapture = this._onClickCapture.bind(this);
      this._onSubmitCapture = this._onSubmitCapture.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onCartError = this._onCartError.bind(this);
      this._poll = this._poll.bind(this);
    }

    async init() {
      // Load static variants from the Liquid-rendered <script data-all-variants> JSON
      // FIRST. This is our authoritative source for variant.options strings (they
      // match the radio.value strings exactly, since both come from the same Liquid
      // render) and for inventory_quantity (which /products/{handle}.js sometimes
      // omits for certain products — undocumented Shopify quirk).
      this._loadStaticVariants();

      // Also capture per-option-value inventory from the radio data-variant-quantity
      // attributes. This is a third fallback for products where neither
      // /products/{handle}.js nor the static variants JSON includes inventory_quantity.
      this._loadInventoryFromDOM();

      // Then fetch real-time data
      await this._refreshStock();

      // Wire DOM listeners (capture phase so we run before theme handlers)
      this.productInfo.addEventListener('change', this._onChange, true);
      this.productInfo.addEventListener('click', this._onClickCapture, true);

      // Form submit interception — find the main product form
      const form = this.productInfo.querySelector(
        `form#product-form-${this.section}, form[action$="/cart/add"]`
      );
      if (form) {
        form.addEventListener('submit', this._onSubmitCapture, true);
        this._submitForm = form;
      }

      // PubSub — theme-driven events
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this.unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange);
        this.unsubError = subscribe(PUB_SUB_EVENTS.cartError, this._onCartError);
      }

      // Visibility / focus listeners — when user returns from another tab (e.g.,
      // the Shopify admin where they just zeroed inventory), refresh stock immediately.
      // This collapses the "7-8s waiting for next poll" window down to <1s.
      this._onVisibility = () => {
        if (document.visibilityState === 'visible') {
          this._refreshStock();
          this._scheduleNextPoll();
        }
      };
      this._onFocus = () => {
        this._refreshStock();
        this._scheduleNextPoll();
      };
      document.addEventListener('visibilitychange', this._onVisibility);
      window.addEventListener('focus', this._onFocus);

      // Begin polling
      this._scheduleNextPoll();
      log('init complete', this.handle);
    }

    destroy() {
      clearTimeout(this.pollTimer);
      this.productInfo.removeEventListener('change', this._onChange, true);
      this.productInfo.removeEventListener('click', this._onClickCapture, true);
      this._submitForm?.removeEventListener('submit', this._onSubmitCapture, true);
      this.unsubVariant?.();
      this.unsubError?.();
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._onFocus) window.removeEventListener('focus', this._onFocus);
      log('destroyed', this.handle);
    }

    // ---------- STOCK FETCH + UI APPLY ----------

    async _refreshStock() {
      if (!this.handle) return;
      const url = `/products/${this.handle}.js?_=${Date.now()}`;
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(url, {
          cache: 'no-store',
          signal: ctrl.signal,
          headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', Pragma: 'no-cache' },
        });
        clearTimeout(timer);
        if (!res.ok) { log('fetch failed status', res.status); return; }
        this.productData = await res.json();
        this._applyStockUI();
      } catch (e) {
        clearTimeout(timer);
        log('fetch error', e?.name || e);
      }
    }

    _scheduleNextPoll() {
      clearTimeout(this.pollTimer);
      this.pollTimer = setTimeout(this._poll, POLL_INTERVAL_MS);
    }

    async _poll() {
      // If the product-info element was removed from the DOM (e.g., combined-listings swap),
      // self-destruct so we don't keep fetching for a dead product.
      if (!this.productInfo.isConnected) {
        this.destroy();
        return;
      }
      await this._refreshStock();
      this._scheduleNextPoll();
    }

    _applyStockUI() {
      if (!this.productData && !this.staticVariants) return;
      const selection = this._readCurrentSelection();
      this._applyRadioAvailability(selection);
      this._applyHeroAvailability(selection);

      const currentVariant = this._findVariant(selection);
      this._updateInventoryDisplay(currentVariant);
      this._updateSubmitButton(currentVariant);
      this._forceVariantInput(currentVariant);
    }

    // ---------- DOM READING ----------

    /**
     * The theme's HTMLUpdateUtility.viewTransition uses a double-buffer pattern:
     * during a 500ms window after a variant change, BOTH the old and new
     * <product-variant-selects> exist in the DOM — the old one with `display: none`
     * but its radios still :checked. Reading from all of them yields duplicate
     * option values and breaks variant matching. This helper returns the visible one.
     */
    _getActiveVariantSelects() {
      const all = this.productInfo.querySelectorAll('product-variant-selects');
      if (!all.length) return null;
      for (let i = 0; i < all.length; i++) {
        if (all[i].style.display !== 'none') return all[i];
      }
      return all[0]; // fallback if all are hidden (shouldn't happen)
    }

    /**
     * Read the current option-value selection from the DOM in option-position order.
     * Returns array like ['Blue', 'Siesta 3'] for a 2-option product, or ['Cruisin 318'] for 1-option.
     * `undefined` at a position means nothing is checked for that option.
     */
    _readCurrentSelection() {
      const pvs = this._getActiveVariantSelects();
      if (!pvs) return [];
      const selection = [];
      const containers = pvs.querySelectorAll(
        ':scope > .product-form__input, :scope > fieldset.product-form__input'
      );
      containers.forEach((container) => {
        if (container.tagName === 'FIELDSET') {
          const checked = container.querySelector('input[type="radio"]:checked');
          selection.push(checked ? checked.value : undefined);
        } else {
          const select = container.querySelector('select');
          selection.push(select && select.value ? select.value : undefined);
        }
      });
      return selection;
    }

    // ---------- AVAILABILITY LOGIC ----------

    _loadStaticVariants() {
      // The variant-picker snippet emits: <script data-all-variants>{{ product.variants | json }}</script>
      // This is the Liquid-rendered authoritative copy of all variants — strings match
      // radio.value exactly (same Liquid source), and inventory_quantity is always present.
      const script = this.productInfo.querySelector(
        'product-variant-selects script[data-all-variants]'
      );
      if (!script) {
        log('No <script data-all-variants> found — fallback unavailable');
        return;
      }
      try {
        const data = JSON.parse(script.textContent);
        if (Array.isArray(data) && data.length) {
          this.staticVariants = data;
          log('Loaded', data.length, 'static variants. Sample fields:', Object.keys(data[0] || {}));
          log('Sample variant inventory_quantity=', data[0].inventory_quantity, 'inventory_management=', data[0].inventory_management);
        }
      } catch (e) {
        log('Failed to parse data-all-variants JSON', e);
      }
    }

    /**
     * For each radio in the standard picker, the Liquid template emits:
     *   <input ... data-variant-quantity="{{ value.variant.inventory_quantity }}">
     *
     * For 1-option products (Cruisin / Siesta / Classic), this is exact per-variant.
     * For 2-option products, it's the first variant matching that option value (less
     * useful but still a hint). We capture this map as a third-tier fallback.
     */
    _loadInventoryFromDOM() {
      this._inventoryByOptionValue = {};
      const radios = this.productInfo.querySelectorAll(
        'input[type="radio"][data-variant-quantity]'
      );
      radios.forEach((radio) => {
        const qty = parseInt(radio.dataset.variantQuantity, 10);
        if (Number.isFinite(qty) && radio.value) {
          this._inventoryByOptionValue[radio.value] = qty;
        }
      });
      log('Loaded inventory from DOM data-variant-quantity:', this._inventoryByOptionValue);
    }

    /**
     * Multi-source qty resolver. Tries in order:
     *   1. productData (real-time, /products/{handle}.js)
     *   2. staticVariants (Liquid render, <script data-all-variants>)
     *   3. The variant object passed in
     *   4. DOM data-variant-quantity attribute (for 1-option products: exact)
     *
     * Returns null if no valid number found anywhere.
     */
    _resolveQty(variant) {
      if (!variant) return null;
      const id = variant.id;

      if (this.productData && this.productData.variants) {
        const live = this.productData.variants.find((v) => v.id === id);
        if (live && Number.isFinite(live.inventory_quantity)) {
          return live.inventory_quantity;
        }
      }
      if (this.staticVariants) {
        const stat = this.staticVariants.find((v) => v.id === id);
        if (stat && Number.isFinite(stat.inventory_quantity)) {
          return stat.inventory_quantity;
        }
      }
      if (Number.isFinite(variant.inventory_quantity)) {
        return variant.inventory_quantity;
      }
      // DOM fallback: works perfectly for 1-option products (Cruisin)
      if (
        this._inventoryByOptionValue &&
        variant.options &&
        variant.options.length === 1
      ) {
        const qty = this._inventoryByOptionValue[variant.options[0]];
        if (Number.isFinite(qty)) return qty;
      }
      return null;
    }

    /**
     * Returns the freshest known data for the given variant by ID.
     *
     * Always starts the merge from staticVariants (Liquid render — has every field
     * fully populated), then overlays productData (real-time) where it has defined
     * values. This handles the Shopify quirk where /products/{handle}.js returns
     * null for fields like inventory_management on certain products.
     *
     * Critical detail: we cannot trust the passed-in `variant` as the merge base —
     * it might already BE a productData variant (from _findVariant), with the same
     * null fields we're trying to fix.
     */
    _getLiveVariant(variant) {
      if (!variant) return null;

      const staticVar =
        this.staticVariants &&
        this.staticVariants.find((v) => v.id === variant.id);
      const liveVar =
        this.productData &&
        this.productData.variants &&
        this.productData.variants.find((v) => v.id === variant.id);

      // Base = static if available (fully-populated by Liquid), else the passed variant
      const base = staticVar || variant;
      if (!liveVar) return base;

      // Overlay live's defined values onto the static base
      const merged = Object.assign({}, base);
      Object.keys(liveVar).forEach((k) => {
        if (liveVar[k] !== null && liveVar[k] !== undefined) {
          merged[k] = liveVar[k];
        }
      });
      return merged;
    }

    /**
     * Find the variant whose .options array exactly matches `optionValues` in order.
     * Tries productData first (real-time), then staticVariants (Liquid fallback).
     * Returns null if no match (e.g., user selected a non-existent combination).
     */
    _findVariant(optionValues) {
      if (!optionValues.some((v) => v != null)) return null;

      const matchFn = (v) => {
        const vOpts = v.options || [];
        for (let i = 0; i < Math.max(vOpts.length, optionValues.length); i++) {
          if (optionValues[i] == null) continue;
          if (vOpts[i] !== optionValues[i]) return false;
        }
        return true;
      };

      // Try real-time data first
      if (this.productData && this.productData.variants) {
        const v = this.productData.variants.find(matchFn);
        if (v) return v;
      }

      // Fall back to static Liquid data — strings are guaranteed to match the
      // radio.value because both come from the same Liquid template render.
      if (this.staticVariants) {
        const v = this.staticVariants.find(matchFn);
        if (v) {
          log('_findVariant: matched via static fallback for', optionValues);
          return v;
        }
      }

      log('_findVariant: NO MATCH for', optionValues);
      return null;
    }

    /**
     * Shopify "graph node" availability rule:
     *   An option value at position N is available iff there exists at least one variant where:
     *     - option[N] === value
     *     - options at positions < N match currentSelection (where set)
     *     - variant.available === true
     */
    _isOptionValueAvailable(optionIndex, value, currentSelection) {
      // Prefer real-time variants for accurate `available` flag.
      // Static variants reflect the page-load snapshot — useful only as last resort.
      const variants =
        (this.productData && this.productData.variants) || this.staticVariants;
      if (!variants) return true; // optimistic when no data
      return variants.some((v) => {
        const vOpts = v.options || [];
        if (vOpts[optionIndex] !== value) return false;
        for (let i = 0; i < optionIndex; i++) {
          if (currentSelection[i] != null && vOpts[i] !== currentSelection[i]) return false;
        }
        return v.available === true;
      });
    }

    // ---------- STANDARD PICKER (radios + selects) ----------

    _applyRadioAvailability(selection) {
      const pvs = this._getActiveVariantSelects();
      if (!pvs) return;

      const fieldsets = pvs.querySelectorAll(':scope > fieldset.product-form__input');
      fieldsets.forEach((fieldset, optionIndex) => {
        fieldset.querySelectorAll('input[type="radio"].product-option__input').forEach((input) => {
          const value = input.value;
          const isAvail = this._isOptionValueAvailable(optionIndex, value, selection);
          if (!isAvail) {
            input.disabled = true;
            input.classList.add('disabled');           // theme already styles `.disabled`
            input.classList.add(SOLD_OUT_CLASS);        // our flag for "we disabled this"
          } else if (input.classList.contains(SOLD_OUT_CLASS)) {
            // Restock case: only re-enable if WE disabled it
            input.disabled = false;
            input.classList.remove('disabled');
            input.classList.remove(SOLD_OUT_CLASS);
          }
        });
      });

      // Dropdown picker (rare, but supported by theme)
      const dropdownContainers = pvs.querySelectorAll(':scope > .product-form__input--dropdown');
      dropdownContainers.forEach((container, idx) => {
        // Map dropdown index to actual option index by counting fieldsets before it
        let optionIndex = 0;
        let prev = container.previousElementSibling;
        while (prev) {
          if (prev.matches('.product-form__input')) optionIndex++;
          prev = prev.previousElementSibling;
        }
        const select = container.querySelector('select');
        if (!select) return;
        Array.from(select.options).forEach((opt) => {
          const value = opt.value;
          const isAvail = this._isOptionValueAvailable(optionIndex, value, selection);
          opt.disabled = !isAvail;
          opt.classList.toggle(SOLD_OUT_CLASS, !isAvail);
        });
      });
    }

    // ---------- HERO VARIANT PICKER (custom block) ----------

    _applyHeroAvailability(selection) {
      const heroBtns = document.querySelectorAll('.js-hero-variant-btn');
      if (!heroBtns.length) return;

      // Need either real-time or static variants to determine option positions.
      const variants =
        (this.productData && this.productData.variants) || this.staticVariants;
      if (!variants || !variants.length) return;

      // Discover which option index the hero picker represents (same for all buttons).
      // Walk option positions and find the one whose values include the hero's first button value.
      const firstValue = heroBtns[0].dataset.value;
      const numPositions = (variants[0].options || []).length;
      let heroOptionIndex = -1;
      for (let i = 0; i < numPositions; i++) {
        const valuesAtI = variants.map((v) => (v.options || [])[i]);
        if (valuesAtI.includes(firstValue)) {
          heroOptionIndex = i;
          break;
        }
      }
      if (heroOptionIndex === -1) return;

      heroBtns.forEach((btn) => {
        const value = btn.dataset.value;
        const isAvail = this._isOptionValueAvailable(heroOptionIndex, value, selection);
        btn.classList.toggle(SOLD_OUT_CLASS, !isAvail);
        if (!isAvail) {
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('tabindex', '-1');
        } else {
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('tabindex');
        }
      });
    }

    // ---------- INVENTORY NOTICE ("Only X left") ----------

    _updateInventoryDisplay(variant) {
      const el = this.productInfo.querySelector('[data-inventory-notice]');
      if (!el) return;

      // Cache the icon SVG once. The icon is variant-independent, so capturing it
      // from the initial server render is safe and survives later DOM swaps.
      if (this._inventoryIconHTML === undefined) {
        const svg = el.querySelector('svg');
        this._inventoryIconHTML = svg ? svg.outerHTML : '';
      }

      if (!variant) {
        el.classList.add('hidden');
        return;
      }

      // Use the merged live+static view so we always have inventory_quantity
      // (productData sometimes omits it for certain products — fall back to static).
      const live = this._getLiveVariant(variant);
      const qty = this._resolveQty(variant);
      const tracked = (live && live.inventory_management === 'shopify') ||
        (qty !== null && variant.id !== undefined); // if we have a qty and a variant, treat as tracked
      const available = !!(live && live.available);
      const validQty = Number.isFinite(qty) && qty > 0;

      const thresholdRaw = parseInt(el.dataset.inventoryThreshold, 10);
      const threshold = Number.isFinite(thresholdRaw) ? thresholdRaw : 0;

      // Mirror the Liquid rule: visible iff
      //   variant exists AND available AND tracked AND qty > 0
      //   AND (threshold <= 0 OR qty <= threshold).
      const aboveThreshold = threshold > 0 && validQty && qty > threshold;
      const willHide = !available || !tracked || !validQty || aboveThreshold;

      log('_updateInventoryDisplay:', {
        variantId: variant.id,
        qty,
        tracked,
        available,
        threshold,
        validQty,
        aboveThreshold,
        willHide,
      });

      if (willHide) {
        el.classList.add('hidden');
        return;
      }

      // Re-render fresh from translation template + cached icon.
      const tmpl =
        (window.flashSaleStrings && window.flashSaleStrings.inventoryLowStockShowCount) ||
        'Only __QTY__ left in stock. Order soon.';
      const newText = tmpl.replace('__QTY__', String(qty));
      el.innerHTML = this._inventoryIconHTML + newText;
      el.dataset.inventoryQuantity = String(qty);
      el.classList.remove('hidden');
    }

    // ---------- SUBMIT BUTTON ----------

    _updateSubmitButton(variant) {
      const submitBtn = this.productInfo.querySelector('button[type="submit"][name="add"]');
      if (!submitBtn) return;
      const submitText = submitBtn.querySelector('span');

      // Don't fight the theme's mid-fetch loading spinner state. If the cart is being
      // submitted right now, leave the button alone — our submit interceptor handles that.
      if (submitBtn.classList.contains('loading')) {
        log('_updateSubmitButton: button in loading state, skipping');
        return;
      }

      const live = variant ? this._getLiveVariant(variant) : null;
      const soldOut = !live || !live.available;
      log('_updateSubmitButton:', {
        variantPassed: variant && variant.id,
        liveResolved: live && live.id,
        liveAvailable: live && live.available,
        decision: soldOut ? 'SOLD_OUT' : 'ADD_TO_CART',
      });

      if (soldOut) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('disabled', 'disabled');
        submitBtn.setAttribute('aria-disabled', 'true');
        if (submitText) {
          submitText.textContent = window.variantStrings?.soldOut || 'Esgotado';
        }
        submitBtn.classList.add(SOLD_OUT_CLASS);
      } else {
        // Variant is available — ALWAYS sync to "ready to add" state, regardless of
        // who set the previous state (theme's resetProductFormState commonly leaves
        // the button disabled with stale "Sold out" text during the 3-4s fetch).
        submitBtn.disabled = false;
        submitBtn.removeAttribute('disabled');
        submitBtn.removeAttribute('aria-disabled');
        if (submitText) {
          const productForm = submitBtn.closest('product-form');
          submitText.textContent = productForm?.hasAttribute('data-has-selling-plan')
            ? window.variantStrings?.addSubscriptionToCart || 'Add subscription to cart'
            : window.variantStrings?.addToCart || 'Add to cart';
        }
        submitBtn.classList.remove(SOLD_OUT_CLASS);
      }
    }

    // ---------- FORCE INPUT TO USER'S CHOSEN VARIANT ----------

    _forceVariantInput(variant) {
      if (!variant) return;
      this.productInfo
        .querySelectorAll('product-form input[name="id"], form input[name="id"].product-variant-id')
        .forEach((input) => {
          if (input.value !== String(variant.id)) {
            input.value = String(variant.id);
          }
        });
    }

    // ---------- EVENT HANDLERS ----------

    _onChange(event) {
      const t = event.target;
      const isPickerInput =
        (t.matches && t.matches('input[type="radio"].product-option__input')) ||
        (t.tagName === 'SELECT' && t.closest('product-variant-selects'));
      if (!isPickerInput) return;

      log('_onChange: target value=', t.value);

      // Kick off a fresh stock check in parallel. This catches the case where
      // user just came back from the admin (zeroed inventory) and immediately
      // clicked a variant — we want fresh data, not the up-to-2s-old polling cache.
      // The fetch runs concurrently with the immediate-feedback microtask below.
      this._refreshStock();

      // Schedule a microtask. This runs AFTER the theme's bubble-phase listeners
      // have processed the change (including resetProductFormState, which disables
      // the submit button), but BEFORE the theme's Section Rendering fetch resolves
      // (which takes 3-4s in flash-sale traffic).
      //
      // Using cached productData (or staticVariants fallback), we update the submit
      // button text + state and the inventory notice IMMEDIATELY — no waiting for
      // the theme's slow fetch.
      Promise.resolve().then(() => {
        if (!this.productInfo.isConnected) return;
        if (!this.productData && !this.staticVariants) {
          log('_onChange microtask: no data sources available, skipping');
          return;
        }

        const selection = this._readCurrentSelection();
        const variant = this._findVariant(selection);
        log('_onChange microtask: selection=', selection, 'variant=', variant && variant.id);

        this._forceVariantInput(variant);
        this._updateSubmitButton(variant);
        this._updateInventoryDisplay(variant);
        this._applyRadioAvailability(selection);
        this._applyHeroAvailability(selection);
      });
    }

    _onClickCapture(event) {
      // Block clicks on hero buttons we marked sold-out
      const heroBtn = event.target.closest && event.target.closest('.js-hero-variant-btn');
      if (heroBtn && heroBtn.classList.contains(SOLD_OUT_CLASS)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        // Optionally flash a visual hint — keep simple for now
        heroBtn.animate(
          [{ transform: 'translateX(0)' }, { transform: 'translateX(-3px)' }, { transform: 'translateX(3px)' }, { transform: 'translateX(0)' }],
          { duration: 200, iterations: 1 }
        );
      }
    }

    _onVariantChange(payload) {
      // Theme just finished swapping <product-variant-selects> + updating input/URL.
      // The new DOM may have radio :checked pointing to a different (server-resolved) variant.
      // Wait for viewTransition double-buffer to settle, then re-impose our truth.
      setTimeout(() => {
        if (!this.productInfo.isConnected) return;
        this._applyStockUI();
      }, POST_RENDER_DELAY_MS);
    }

    _onCartError(payload) {
      const errors = payload?.errors;
      const message = payload?.message || '';
      const description = typeof errors === 'string' ? errors : '';
      const haystack = (description + ' ' + message).toLowerCase();
      // Match common sold-out / unavailable patterns across locales
      if (
        /sold[\s-]?out|out of stock|inventory|esgotad|sem estoque|indispon[ií]vel|agotad|épuis/i.test(
          haystack
        )
      ) {
        const selection = this._readCurrentSelection();
        const variant = this._findVariant(selection);
        this._showSoldOutPopup(variant);
        // Force a fresh fetch and re-apply UI immediately — don't wait for next poll
        this._refreshStock();
      }
    }

    async _onSubmitCapture(event) {
      if (this.bypassSubmit) {
        this.bypassSubmit = false;
        return; // re-entry from our own form.requestSubmit()
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitBtn = this.productInfo.querySelector('button[type="submit"][name="add"]');
      const spinner = submitBtn?.querySelector('.loading__spinner');
      submitBtn?.classList.add('loading');
      spinner?.classList.remove('hidden');
      submitBtn?.setAttribute('aria-disabled', 'true');

      // Pre-flight: refresh stock and validate the user's CURRENT selection
      try {
        await this._refreshStock();
      } catch (_) { /* fall through with stale data */ }

      const selection = this._readCurrentSelection();
      const variant = this._findVariant(selection);

      submitBtn?.classList.remove('loading');
      spinner?.classList.add('hidden');

      if (!variant || !variant.available) {
        // Race condition hit: variant sold out between selection and submit
        this._applyStockUI();             // sync UI to new reality
        this._showSoldOutPopup(variant);  // tell user
        return;
      }

      // Variant is good. Force the input to the validated variant ID, then re-trigger
      // submit, bypassing our handler so the theme's native flow runs.
      this._forceVariantInput(variant);
      this.bypassSubmit = true;
      submitBtn?.removeAttribute('aria-disabled');

      const form = event.target;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        // Fallback for older browsers
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }

    // ---------- POPUP ----------

    _showSoldOutPopup(variant) {
      const popup = this._ensurePopup();
      const titleEl = popup.querySelector('.flash-popup__title');
      const messageEl = popup.querySelector('.flash-popup__message');

      titleEl.textContent = window.variantStrings?.soldOut || 'Esgotado';

      const variantTitle = variant?.title || this.productData?.title || '';
      messageEl.textContent = variantTitle
        ? `A variante "${variantTitle}" acabou de esgotar enquanto você decidia. Tente outra opção ou volte mais tarde.`
        : 'Esta variante acabou de esgotar. Tente outra opção ou volte mais tarde.';

      popup.classList.add(POPUP_OPEN_CLASS);
      document.body.classList.add('flash-popup-locked');

      // Trap initial focus on the close/CTA button
      const cta = popup.querySelector('.flash-popup__cta');
      requestAnimationFrame(() => cta?.focus());
    }

    _ensurePopup() {
      // Reuse a single shared popup across validators / page lifetime
      let existing = document.querySelector('.flash-popup');
      if (existing) {
        this.popup = existing;
        return existing;
      }
      const popup = document.createElement('div');
      popup.className = 'flash-popup';
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'true');
      popup.setAttribute('aria-hidden', 'true');
      popup.innerHTML = `
        <div class="flash-popup__overlay" data-flash-close></div>
        <div class="flash-popup__panel" role="document">
          <button type="button" class="flash-popup__close" aria-label="Fechar" data-flash-close>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="flash-popup__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="36" height="36">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" fill="none"/>
              <path d="M12 7v6M12 16.5v.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <h2 class="flash-popup__title"></h2>
          <p class="flash-popup__message"></p>
          <button type="button" class="flash-popup__cta" data-flash-close>Entendi</button>
        </div>
      `;
      document.body.appendChild(popup);

      const close = () => {
        popup.classList.remove(POPUP_OPEN_CLASS);
        document.body.classList.remove('flash-popup-locked');
        popup.setAttribute('aria-hidden', 'true');
      };
      popup.addEventListener('click', (e) => {
        if (e.target.closest('[data-flash-close]')) close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && popup.classList.contains(POPUP_OPEN_CLASS)) close();
      });

      this.popup = popup;
      return popup;
    }
  }

  // ---------- BOOTSTRAP ----------

  const validators = new WeakMap();

  function tryAttach(productInfo) {
    if (!productInfo) return;
    if (validators.has(productInfo)) return;
    if (!productInfo.dataset.productHandle) return;
    if (!productInfo.hasAttribute('data-main-product')) return;

    const validator = new FlashSaleStockValidator(productInfo);
    validators.set(productInfo, validator);
    validator.init();
  }

  function initAll() {
    document.querySelectorAll('product-info[data-main-product]').forEach(tryAttach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Catch new product-info dropped in by the theme (combined-listings swap)
  document.addEventListener('product-info:loaded', (e) => {
    const pi = e.target?.closest?.('product-info[data-main-product]');
    if (pi) tryAttach(pi);
  });

  // Expose for debugging in DevTools.
  // Era `if (DEBUG)`, e DEBUG nunca foi declarado neste arquivo — o proprio
  // comentario da linha 42 diz que a flag virou window.__FSSV_DEBUG. Lancava
  // ReferenceError na ultima linha da IIFE em toda PDP, inclusive na loja
  // publicada (verificado em producao, sem preview).
  if (window.__FSSV_DEBUG === true) window.__flashValidators = validators;
})();